import React, { useState } from "react";
import { Container, Row, Col, Button, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaPlay,
  FaRocket,
  FaMicrochip,
  FaArrowLeft,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
} from "react-icons/fa";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import "../../styles/kids-program.css";

const ArduinoElectronics = () => {
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [showDemoModal, setShowDemoModal] = useState(false);

  const modules = [
    {
      id: 1,
      title: "Introduction to Electronics & Arduino",
      icon: "🔌",
      topics: [
        "What is Arduino and why it's awesome",
        "Understanding basic electronic components (LED, Resistor, Breadboard)",
        "Setting up Arduino IDE on your computer",
        "Understanding Arduino board (Uno, Nano, Mega)",
        "Your first circuit: Making an LED blink",
      ],
    },
    {
      id: 2,
      title: "Arduino Programming Basics",
      icon: "💻",
      topics: [
        "Understanding setup() and loop() functions",
        "Variables and data types in Arduino",
        "Using if-else conditions",
        "Working with for and while loops",
        "Digital vs Analog signals (PWM)",
      ],
    },
    {
      id: 3,
      title: "Working with Sensors",
      icon: "📡",
      topics: [
        "Ultrasonic distance sensor (HC-SR04)",
        "Temperature and humidity sensor (DHT11)",
        "Light sensor (LDR) - automatic night lamp",
        "Sound sensor - clap switch",
        "IR sensor - remote control and obstacle detection",
      ],
    },
    {
      id: 4,
      title: "Motors and Movement",
      icon: "🚗",
      topics: [
        "Understanding servo motors (SG90, MG995)",
        "DC motors and motor drivers (L298N, L293D)",
        "Controlling motor speed with PWM",
        "Building a basic robot car chassis",
        "Making your robot move forward, backward, turn",
      ],
    },
    {
      id: 5,
      title: "Advanced Arduino Projects",
      icon: "🏠",
      topics: [
        "Smart home automation (control lights with phone)",
        "Obstacle avoiding robot",
        "Line following robot with IR sensors",
        "Bluetooth controlled robot car",
        "LCD display - showing sensor data",
      ],
    },
    {
      id: 6,
      title: "Final Project - Build Your Own Robot",
      icon: "🏆",
      topics: [
        "Choose your project (Robot Car, Smart Home, etc.)",
        "Planning and designing your project",
        "Assembling components and writing code",
        "Testing and troubleshooting",
        "Project presentation and certificate",
      ],
    },
  ];

  const projects = [
    { name: "LED Blink", icon: "💡", description: "Your first Arduino program - make an LED blink" },
    { name: "Smart Lamp", icon: "🏠", description: "Automatic light that turns on in darkness" },
    { name: "Distance Meter", icon: "📏", description: "Measure distance using ultrasonic sensor" },
    { name: "Robot Car", icon: "🚗", description: "Build a robot that moves and avoids obstacles" },
  ];

  const faqs = [
    {
      q: "Do I need to buy an Arduino kit?",
      a: "Yes, you'll need an Arduino starter kit. We can provide a kit at additional cost, or you can purchase your own. A basic kit costs around ₹2,500-3,500.",
    },
    {
      q: "What age group is this suitable for?",
      a: "This course is designed for kids aged 10-16. Younger kids may need parent assistance.",
    },
    {
      q: "Do I need prior programming experience?",
      a: "No prior programming experience is required. We'll teach you everything from basics!",
    },
    {
      q: "Will my child get a certificate?",
      a: "Yes, every student receives a completion certificate after finishing the course.",
    },
    {
      q: "What tools do I need?",
      a: "A computer with internet connection, Arduino Uno board, breadboard, jumper wires, LEDs, resistors, sensors, and motors (starter kit includes all).",
    },
  ];

  const toggleModule = (id) => {
    setActiveModule(activeModule === id ? null : id);
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleStartLearning = () => {
    document.getElementById("curriculum")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleWatchDemo = () => setShowDemoModal(true);
  const handleEnrollNow = () => navigate("/register");
  const handleBookDemo = () => navigate("/contact");
  const handleBackToKids = () => navigate("/kids");

  return (
    <div className="kids-program-page">
      <PublicNavbar />

      <section className="kids-program-hero kids-program-hero-blue">
        <div className="kids-program-hero-bubbles">
          <div className="kids-program-bubble"></div>
          <div className="kids-program-bubble"></div>
          <div className="kids-program-bubble"></div>
          <div className="kids-program-bubble"></div>
          <div className="kids-program-bubble"></div>
        </div>
        
        <Container>
          <Button variant="link" className="kids-program-back-btn" onClick={handleBackToKids}>
            <FaArrowLeft className="me-2" /> Back to Kids Programs
          </Button>

          <Row className="align-items-center">
            <Col lg={7}>
              <div className="kids-program-hero-content">
                <div className="kids-program-hero-badge">
                  <span>🤖</span><span>Build Real Electronic Projects!</span><span>⚡</span>
                </div>
                <h1 className="kids-program-hero-title">
                  Learn <span>Arduino</span> & Build Amazing Robots
                </h1>
                <p className="kids-program-hero-description">
                  Discover the exciting world of electronics and robotics. Build real
                  projects like smart home devices, obstacle-avoiding robots, and more!
                  No prior experience needed.
                </p>
                <div className="kids-program-hero-buttons">
                  <Button className="kids-program-btn-primary" onClick={handleStartLearning}>
                    <FaRocket className="me-2" /> Start Learning
                  </Button>
                  <Button className="kids-program-btn-outline" onClick={handleWatchDemo}>
                    <FaPlay className="me-2" /> Watch Demo
                  </Button>
                </div>
                <div className="kids-program-hero-stats">
                  <div className="kids-program-stat"><span className="kids-program-stat-number">10+</span><span className="kids-program-stat-label">Projects Built</span></div>
                  <div className="kids-program-stat"><span className="kids-program-stat-number">15+</span><span className="kids-program-stat-label">Components</span></div>
                  <div className="kids-program-stat"><span className="kids-program-stat-number">100%</span><span className="kids-program-stat-label">Hands-on</span></div>
                </div>
              </div>
            </Col>
            <Col lg={5}>
              <div className="kids-program-hero-illustration">
                <div className="kids-program-floating-icon kids-program-icon-1">🤖</div>
                <div className="kids-program-floating-icon kids-program-icon-2">💡</div>
                <div className="kids-program-floating-icon kids-program-icon-3">🔌</div>
                <div className="kids-program-floating-icon kids-program-icon-4">📡</div>
                <div className="kids-program-floating-icon kids-program-icon-5">🚗</div>
                <div className="kids-program-floating-icon kids-program-icon-6">🔧</div>
                <div className="kids-program-main-icon"><FaMicrochip /></div>
              </div>
            </Col>
          </Row>
        </Container>

        <div className="kids-program-hero-wave">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path fill="#ffffff" fillOpacity="1" d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      <section className="kids-program-learn">
        <Container>
          <div className="kids-program-section-header"><h2>What You'll Learn 🎯</h2><p>By the end of this course, you'll be able to</p></div>
          <Row className="g-4">
            {[
              { icon: "🔌", title: "Electronics Basics", desc: "Understand circuits, LEDs, resistors, and breadboards" },
              { icon: "💻", title: "Arduino Programming", desc: "Write code to control electronic components" },
              { icon: "📡", title: "Sensors & Motors", desc: "Work with ultrasonic, IR, temperature sensors and motors" },
              { icon: "🚗", title: "Robotics Projects", desc: "Build robots that move, sense, and react" }
            ].map((item, i) => (
              <Col md={6} lg={3} key={i}>
                <div className="kids-program-learn-card">
                  <div className="kids-program-learn-icon kids-program-learn-icon-blue">{item.icon}</div>
                  <h4>{item.title}</h4><p>{item.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section id="curriculum" className="kids-program-curriculum">
        <Container>
          <div className="kids-program-section-header"><h2>📚 Course Curriculum</h2><p>6 exciting modules to take you from beginner to robot builder</p></div>
          <div className="kids-program-modules">
            {modules.map((module) => (
              <div key={module.id} className={`kids-program-module kids-program-module-blue ${activeModule === module.id ? "active" : ""}`}>
                <div className="kids-program-module-header" onClick={() => toggleModule(module.id)}>
                  <h3><span className="kids-program-module-icon">{module.icon}</span>Module {module.id}: {module.title}</h3>
                  <span className="kids-program-module-toggle">{activeModule === module.id ? <FaChevronUp /> : <FaChevronDown />}</span>
                </div>
                <div className="kids-program-module-content">
                  <ul>{module.topics.map((topic, idx) => (<li key={idx}><FaCheckCircle className="me-2" style={{ color: "#0056b3" }} />{topic}</li>))}</ul>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="kids-program-projects">
        <Container>
          <div className="kids-program-section-header"><h2>🎮 Projects You'll Build</h2><p>Create these amazing electronic projects and more!</p></div>
          <Row className="g-4">{projects.map((p, i) => (<Col md={6} lg={3} key={i}><div className="kids-program-project-card"><div className="kids-program-project-icon">{p.icon}</div><h4>{p.name}</h4><p>{p.description}</p></div></Col>))}</Row>
        </Container>
      </section>

      <section className="kids-program-why kids-program-why-blue">
        <Container>
          <div className="kids-program-section-header text-white"><h2 className="text-white">Why Choose This Course? 🌟</h2><p className="text-white">The perfect start for young inventors</p></div>
          <div className="kids-program-why-grid">
            {[["🎯", "No Experience Needed", "Perfect for absolute beginners"], ["🤖", "10+ Real Projects", "Build working robots and electronics"], ["🏆", "Certificate", "Get certified on completion"], ["🔧", "Hands-on Learning", "Learn by building real circuits"]].map((item, i) => (
              <div key={i} className="kids-program-why-item"><div className="kids-program-why-icon">{item[0]}</div><h4>{item[1]}</h4><p>{item[2]}</p></div>
            ))}
          </div>
        </Container>
      </section>

      <section className="kids-program-faq">
        <Container>
          <div className="kids-program-section-header"><h2>❓ Frequently Asked Questions</h2><p>Everything parents need to know</p></div>
          <div className="kids-program-faq-container">{faqs.map((faq, idx) => (<div key={idx} className={`kids-program-faq-item ${openFaqIndex === idx ? "active" : ""}`}><div className="kids-program-faq-question" onClick={() => toggleFaq(idx)}><span>{faq.q}</span><span>{openFaqIndex === idx ? <FaChevronUp /> : <FaChevronDown />}</span></div><div className="kids-program-faq-answer"><p>{faq.a}</p></div></div>))}</div>
        </Container>
      </section>

      <section className="kids-program-cta">
        <Container><h2>🤖 Ready to Build Your First Robot?</h2><p>Join young inventors who are already building amazing electronic projects!</p><div className="kids-program-cta-buttons"><Button className="kids-program-cta-primary" onClick={handleEnrollNow}>Enroll Now ✨</Button><Button className="kids-program-cta-secondary" onClick={handleBookDemo}>Book Free Demo 📅</Button></div></Container>
      </section>

      <Modal show={showDemoModal} onHide={() => setShowDemoModal(false)} size="lg" centered className="kids-program-modal">
        <Modal.Header closeButton><Modal.Title><FaPlay className="me-2" /> Arduino & Electronics Demo</Modal.Title></Modal.Header>
        <Modal.Body className="p-0"><div className="video-wrapper"><iframe src="https://www.youtube.com/embed/QnubPziozsw" title="Demo" frameBorder="0" allowFullScreen></iframe></div></Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowDemoModal(false)}>Close</Button></Modal.Footer>
      </Modal>
      <Footer />
    </div>
  );
};

export default ArduinoElectronics;